<template>
  <section id="pos-id-audits" class="pos-id-page">
    <b-card>
      <div class="title d-flex justify-content-between align-items-center flex-wrap">
        <p class="mb-1">POS-ID 審查記錄</p>
        <div class="d-flex flex-wrap mb-1">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="handle-button"
            @click="goManagementCenter"
          >
            回目錄
          </b-button>
          <!-- 匯出同樣要求 server-admin；403 且無 error.code 時整顆按鈕隱藏（guide §6） -->
          <b-button
            v-if="!permissionDenied"
            size="sm"
            variant="outline-success"
            class="handle-button ml-1"
            :disabled="exporting"
            @click="handleExport"
          >
            <i v-if="exporting" class="el-icon-loading mr-1"></i>
            {{ exporting ? '匯出中…' : '匯出 CSV' }}
          </b-button>
        </div>
      </div>

      <b-alert v-if="permissionDenied" show variant="warning" class="mb-3">
        {{ readOnlyHint }}
      </b-alert>
      <b-alert
        v-if="errorText"
        show
        variant="danger"
        dismissible
        class="mb-3"
        @dismissed="clearError"
      >
        {{ errorText }}
      </b-alert>

      <div class="pos-id-filters">
        <b-row>
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">院線（可多選）</label>
            <el-select
              v-model="filters.circuit_id"
              multiple
              filterable
              collapse-tags
              placeholder="請選擇…"
              style="width: 100%"
            >
              <el-option
                v-for="item in circuitOptions"
                :key="item.value"
                :label="item.text"
                :value="item.value"
              />
            </el-select>
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">pos-id</label>
            <div class="d-flex">
              <b-form-input
                v-model="filters.pos_id"
                placeholder="前綴搜尋，例：135"
                @keyup.enter.native="handleSearch"
              />
              <b-form-select
                v-model="filters.pos_id_mode"
                :options="posIdModeOptions"
                class="ml-1"
                style="width: 96px; flex: 0 0 96px"
              />
            </div>
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">film_id</label>
            <b-form-input
              v-model="filters.film_id"
              @keyup.enter.native="handleSearch"
            />
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">film_master_id</label>
            <b-form-input
              v-model="filters.film_master_id"
              @keyup.enter.native="handleSearch"
            />
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">匹配動作（可多選）</label>
            <el-select
              v-model="filters.action"
              multiple
              filterable
              collapse-tags
              placeholder="請選擇…"
              style="width: 100%"
            >
              <el-option
                v-for="item in actionOptions"
                :key="item.value"
                :label="item.text"
                :value="item.value"
              />
            </el-select>
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">創建來源（可多選）</label>
            <el-select
              v-model="filters.source"
              multiple
              filterable
              collapse-tags
              placeholder="請選擇…"
              style="width: 100%"
            >
              <el-option
                v-for="item in sourceOptions"
                :key="item.value"
                :label="item.text"
                :value="item.value"
              />
            </el-select>
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">變動類型</label>
            <b-form-select
              v-model="filters.change_type"
              :options="changeTypeOptions"
            />
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">操作人（前綴）</label>
            <b-form-input
              v-model="filters.operator"
              @keyup.enter.native="handleSearch"
            />
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">日期（起，含當日）</label>
            <b-form-input v-model="filters.date_start" type="date" />
          </b-col>

          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">日期（迄，含當日）</label>
            <b-form-input v-model="filters.date_end" type="date" />
          </b-col>
        </b-row>

        <div class="pos-id-filters__actions">
          <b-button size="sm" variant="primary" class="mr-1" :disabled="loading" @click="handleSearch">
            查詢
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="handleReset">
            重置
          </b-button>
        </div>
      </div>

      <div class="pos-id-table-bar">
        <span class="pos-id-table-bar__title">審查記錄</span>
        <span class="pos-id-table-bar__hint">
          點擊帶 <i class="el-icon-sort"></i> 的表頭可切換排序（↑ 升序 / ↓ 降序）
        </span>
      </div>

      <AuditTable
        :rows="rows"
        :loading="loading"
        :sort-key="sort"
        :sort-direct="direct"
        @show-film-circuit="goFilmCircuitIds"
        @show-special-id="goSpecialIds"
        @sort-changed="handleHeaderSort"
      />

      <pos-id-pagination
        :page="page"
        :count="count"
        :total="total"
        @page-change="handlePageChange"
        @count-change="handleCountChange"
      />
    </b-card>
  </section>
</template>

<script>
import { getCircuitList } from '@/api/circuit'
import { downloadPosIdAuditsCsv, getPosIdAudits } from '@/api/posId'
import {
  AUDIT_ACTION_OPTIONS,
  AUDIT_CHANGE_TYPE_OPTIONS,
  AUDIT_SOURCE_OPTIONS,
  POS_ID_READ_ONLY_HINT,
  buildPosIdAuditQuery,
  resolvePosIdError
} from '@/utils/posId'
import AuditTable from './components/AuditTable.vue'
import PosIdPagination from './components/PosIdPagination.vue'

const POS_ID_MODE_OPTIONS = [
  { text: '前綴', value: 'prefix' },
  { text: '精確', value: 'exact' }
]
const CHANGE_TYPE_OPTIONS = [{ text: '全部', value: '' }].concat(
  AUDIT_CHANGE_TYPE_OPTIONS
)

function createDefaultFilters() {
  return {
    circuit_id: [],
    pos_id: '',
    pos_id_mode: 'prefix',
    film_id: '',
    film_master_id: '',
    action: [],
    source: [],
    change_type: '',
    operator: '',
    date_start: '',
    date_end: ''
  }
}

function isBlankText(value) {
  return value === undefined || value === null || String(value).trim() === ''
}

function toTrimmedText(value) {
  if (value === undefined || value === null || Array.isArray(value)) {
    return ''
  }
  return String(value).trim()
}

function toValueList(value) {
  if (Array.isArray(value)) {
    return value.map((item) => toTrimmedText(item)).filter((item) => item !== '')
  }
  const text = toTrimmedText(value)
  if (text === '') {
    return []
  }
  return text
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item !== '')
}

function buildCircuitText(item) {
  if (isBlankText(item.circuit_name)) {
    return String(item.circuit_key)
  }
  return `${item.circuit_key} - ${item.circuit_name}`
}

function pickKnownValues(list, options) {
  return list.filter((value) => options.some((option) => option.value === value))
}

/** 表列 payload：列表是 {result,total,data:[]}，院線是 {result,data:[]}（兩種都兜底） */
function extractListPayload(res) {
  if (Array.isArray(res)) {
    return res
  }
  if (!res) {
    return []
  }
  if (Array.isArray(res.data)) {
    return res.data
  }
  if (res.data && Array.isArray(res.data.data)) {
    return res.data.data
  }
  return []
}

function readBlobText(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => {
      resolve(event && event.target ? event.target.result : '')
    }
    reader.onerror = () => {
      reject(new Error('讀取回應內容失敗'))
    }
    reader.readAsText(blob)
  })
}

/**
 * 匯出請求的 responseType 是 blob，error.response.data 是 Blob，
 * 直接丟給 resolvePosIdError 會讀不到 error.code（10040 會被誤判成「無權限」）。
 * 因此先解析 Blob 內的 JSON，再交給 resolvePosIdError 統一映射中文（guide §1.3）。
 */
function parseBlobError(error) {
  const response = error && error.response ? error.response : null
  const data = response ? response.data : null
  const isBlob = typeof Blob !== 'undefined' && data instanceof Blob
  if (!isBlob) {
    return Promise.resolve({ info: resolvePosIdError(error), parsedBody: false })
  }
  return readBlobText(data)
    .then((text) => {
      const body = JSON.parse(text)
      return {
        info: resolvePosIdError({
          response: { status: response.status, data: body }
        }),
        parsedBody: true
      }
    })
    .catch(() => {
      const info = Object.assign({}, resolvePosIdError(error), {
        isPermissionDenied: false
      })
      return { info, parsedBody: false }
    })
}

function buildRecordQuery(row) {
  const query = {}
  if (row && !isBlankText(row.pos_id)) {
    query.pos_id = String(row.pos_id)
  }
  if (row && !isBlankText(row.circuit_id)) {
    query.circuit_id = String(row.circuit_id)
  }
  return query
}

export default {
  name: 'PosIdAuditPage',
  components: {
    AuditTable,
    PosIdPagination
  },
  data() {
    return {
      filters: createDefaultFilters(),
      page: 1,
      count: 20,
      sort: 'id',
      direct: 'desc',
      rows: [],
      total: 0,
      loading: false,
      exporting: false,
      permissionDenied: false,
      errorText: '',
      readOnlyHint: POS_ID_READ_ONLY_HINT,
      circuitOptions: [],
      actionOptions: AUDIT_ACTION_OPTIONS,
      sourceOptions: AUDIT_SOURCE_OPTIONS,
      changeTypeOptions: CHANGE_TYPE_OPTIONS,
      posIdModeOptions: POS_ID_MODE_OPTIONS
    }
  },
  created() {
    this.applyRouteQuery()
    this.loadCircuitOptions()
    this.fetchList()
  },
  methods: {
    /** 從別的頁面跳進來時（$route.query.pos_id / circuit_id）預填篩選並自動查詢 */
    applyRouteQuery() {
      const query = (this.$route && this.$route.query) || {}
      const filters = createDefaultFilters()
      filters.pos_id = toTrimmedText(query.pos_id)
      filters.circuit_id = toValueList(query.circuit_id)
      filters.film_id = toTrimmedText(query.film_id)
      filters.film_master_id = toTrimmedText(query.film_master_id)
      filters.action = pickKnownValues(
        toValueList(query.action),
        AUDIT_ACTION_OPTIONS
      )
      filters.source = pickKnownValues(
        toValueList(query.source),
        AUDIT_SOURCE_OPTIONS
      )
      const changeType = toTrimmedText(query.change_type)
      filters.change_type = pickKnownValues([changeType], AUDIT_CHANGE_TYPE_OPTIONS)[0] || ''
      filters.operator = toTrimmedText(query.operator)
      filters.date_start = toTrimmedText(query.date_start)
      filters.date_end = toTrimmedText(query.date_end)
      if (filters.pos_id !== '' && query.pos_id_mode === 'exact') {
        filters.pos_id_mode = 'exact'
      }
      this.filters = filters
    },
    loadCircuitOptions() {
      getCircuitList()
        .then((res) => {
          this.circuitOptions = extractListPayload(res)
            .filter((item) => item && !isBlankText(item.circuit_key))
            .map((item) => ({
              value: String(item.circuit_key),
              text: buildCircuitText(item)
            }))
        })
        .catch(() => {
          // 院線下拉只是輔助條件：載入失敗不影響頁面（仍可用 pos-id / 日期等查詢）
          this.circuitOptions = []
        })
    },
    buildFilterParams() {
      return Object.assign({}, this.filters, {
        page: this.page,
        count: this.count,
        sort: this.sort,
        direct: this.direct
      })
    },
    fetchList() {
      this.loading = true
      const params = buildPosIdAuditQuery(this.buildFilterParams())
      getPosIdAudits(params)
        .then((res) => {
          this.rows = extractListPayload(res)
          this.total = res && typeof res.total === 'number' ? res.total : 0
          this.permissionDenied = false
          this.errorText = ''
          this.loading = false
        })
        .catch((error) => {
          const info = resolvePosIdError(error)
          this.rows = []
          this.total = 0
          this.loading = false
          if (info.isPermissionDenied) {
            this.permissionDenied = true
            this.errorText = POS_ID_READ_ONLY_HINT
          } else {
            this.errorText = info.message
          }
        })
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.filters = createDefaultFilters()
      this.page = 1
      this.count = 20
      this.sort = 'id'
      this.direct = 'desc'
      this.errorText = ''
      this.fetchList()
    },
    handlePageChange(newPage) {
      this.page = Number(newPage) || 1
      this.fetchList()
    },
    handleCountChange(newCount) {
      this.count = Number(newCount) || 20
      this.page = 1
      this.fetchList()
    },
    handleHeaderSort(sortKey) {
      if (!sortKey) {
        return
      }
      if (this.sort === sortKey) {
        this.direct = this.direct === 'asc' ? 'desc' : 'asc'
      } else {
        this.sort = sortKey
        this.direct = 'desc'
      }
      this.page = 1
      this.fetchList()
    },
    clearError() {
      this.errorText = ''
    },
    /**
     * 匯出：必須走 fetch/blob（window.open 不帶 Authorization → 401，guide §7.3-7）
     * 調用凍結契約 downloadPosIdAuditsCsv(params, filters)（內部已處理檔名 + 觸發下載）。
     */
    handleExport() {
      if (this.exporting || this.permissionDenied) {
        return
      }
      this.exporting = true
      this.errorText = ''
      const params = buildPosIdAuditQuery(this.buildFilterParams())
      // 匯出不分頁（後端忽略 page/count，guide §4.2）：直接移除避免誤解
      delete params.page
      delete params.count
      downloadPosIdAuditsCsv(params, this.filters)
        .then((filename) => {
          this.exporting = false
          this.$message.success(`已開始下載：${filename}`)
        })
        .catch((error) => {
          this.exporting = false
          this.handleExportError(error)
        })
    },
    handleExportError(error) {
      parseBlobError(error).then((parsed) => {
        const info = parsed.info
        if (info.code === 10040) {
          let backendText = ''
          if (info.backendMessage) {
            backendText = ` 後端訊息：${info.backendMessage}`
          }
          this.errorText = `匯出失敗：請縮小日期範圍後再匯出。${info.message}${backendText}`
          this.$message.error('請縮小日期範圍後再匯出')
          return
        }
        if (parsed.parsedBody && info.isPermissionDenied) {
          this.permissionDenied = true
          this.errorText = POS_ID_READ_ONLY_HINT
          return
        }
        let text = info.message
        if (info.isPermissionDenied && !parsed.parsedBody) {
          text = '匯出失敗：請確認 server-admin 權限，或縮小日期範圍後重試。'
        }
        this.errorText = `匯出失敗：${text}`
        this.$message.error(text)
      })
    },
    goManagementCenter() {
      this.$router.push({ name: 'management-center' })
    },
    goFilmCircuitIds(row) {
      this.$router.push({
        name: 'pos-id-film-circuit-ids',
        query: buildRecordQuery(row)
      })
    },
    goSpecialIds(row) {
      this.$router.push({
        name: 'pos-id-special-ids',
        query: buildRecordQuery(row)
      })
    }
  }
}
</script>
